/**
 * Per-locale rendering of a `TaskPhrase` — the structured form of "what this
 * agent last did", produced by `live-fold.ts` from a single verified event.
 *
 * Why this module exists: the fold turns an event into one line of prose, and
 * that line used to be English machine text that every locale rendered
 * verbatim. A Russian visitor therefore read an English sentence under a LIVE
 * badge. Translating the finished sentence is not possible without guessing;
 * translating the TEMPLATE is exact, because the engine's own data (model
 * name, peer id, tick, task family, token count) travels in `p` and is
 * interpolated identically in both languages. Nothing is invented here: a
 * phrase that has no data has no data in either locale.
 *
 * What stays Latin in both locales, deliberately: machine tokens the rest of
 * the site also keeps untranslated — tick references (`t42`), model names,
 * thinking tiers, agent ids, task families, action names and raw event types.
 * Only the prose around them is translated, the same convention
 * `ai/terminal.ts` and `terminal-freshness-labels.ts` already follow.
 *
 * Pure module: no Next/React/store imports and only type imports elsewhere —
 * importable directly under `node --test --experimental-strip-types`, and
 * safe to reach from a Client Component.
 */

import type { TaskPhrase, TaskPhraseKey } from './types.ts';
import type { Lang } from '../i18n/config';

type Params = Readonly<Record<string, string | number | boolean>>;

const s = (p: Params, key: string): string => {
  const v = p[key];
  return typeof v === 'string' ? v : '';
};
const n = (p: Params, key: string): number | null => {
  const v = p[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

/** `1.9k tok` / `500 tok`, with the unit translated and the number untouched. */
function tokens(p: Params, unit: string): string {
  const v = n(p, 'tok');
  if (v === null) return '';
  return v >= 1000 ? ` · ${(v / 1000).toFixed(1)}k ${unit}` : ` · ${v} ${unit}`;
}

/** ` · deliberate` — the engine's own tier token, never translated. */
function tier(p: Params): string {
  const v = s(p, 'tier');
  return v === '' ? '' : ` · ${v}`;
}

function quality(p: Params, word: string): string {
  const v = n(p, 'qualityPct');
  return v === null ? '' : ` · ${word} ${v} %`;
}

interface Vocab {
  /** Fallback when the engine named no counterpart. */
  peer: string;
  actionsNone: string;
  unknownAction: string;
  unknownReason: string;
  rejectedReason: string;
  pressureApplied: string;
  tokUnit: string;
  qualityWord: string;
  verdict: { valid: string; invalid: string; unknown: string };
}

const VOCAB: Record<Lang, Vocab> = {
  en: {
    peer: 'a peer',
    actionsNone: 'no actions',
    unknownAction: 'unknown',
    unknownReason: 'unknown',
    rejectedReason: 'rejected',
    pressureApplied: 'applied',
    tokUnit: 'tok',
    qualityWord: 'quality',
    verdict: { valid: 'valid', invalid: 'invalid', unknown: 'unknown' },
  },
  ru: {
    peer: 'партнёром',
    actionsNone: 'без действий',
    unknownAction: 'неизвестно',
    unknownReason: 'причина неизвестна',
    rejectedReason: 'отклонено',
    pressureApplied: 'применено',
    tokUnit: 'ток',
    qualityWord: 'качество',
    verdict: { valid: 'верно', invalid: 'неверно', unknown: 'неизвестно' },
  },
};

type Template = (p: Params, v: Vocab) => string;

const EN: Record<TaskPhraseKey, Template> = {
  'cognition.consulted': () => 'consulted a model',
  'cognition.unavailable': () => 'model unavailable',
  'cognition.starved': () => 'thinking starved · no budget',
  'cognition.rejected': (p, v) =>
    `thought · ${s(p, 'model')}${tier(p)}${tokens(p, v.tokUnit)} → answer rejected`,
  'cognition.thought': (p, v) =>
    `thought · ${s(p, 'model')}${tier(p)}${tokens(p, v.tokUnit)} → ${s(p, 'actions') || v.actionsNone}`,
  'resource.spent': (p, v) => `action · ${s(p, 'action') || v.unknownAction}`,
  'task.claimed': () => 'claimed a task',
  'task.submitted': () => 'submitted a result',
  'task.evaluated': (p, v) =>
    `task ${p.accepted === true ? 'accepted' : 'rejected'}${quality(p, v.qualityWord)}`,
  'link.created': (p, v) => `linked with ${s(p, 'peer') || v.peer}`,
  'link.removed': (p, v) => `unlinked from ${s(p, 'peer') || v.peer}`,
  'link.used': (p, v) => `link → ${s(p, 'peer') || v.peer} used`,
  'message.sent': (p, v) => `message → ${s(p, 'peer') || v.peer}`,
  'message.delivered': () => 'message received',
  'violation.recorded': (p, v) => `invalid action · ${s(p, 'reason') || v.rejectedReason}`,
  'run.resumed': (p) => `epoch resumed from ${s(p, 'parent')}…`,
  'run.started': () => 'universe started',
  'run.completed': (p) => {
    const ticks = n(p, 'ticks');
    const events = n(p, 'events');
    return `run completed${ticks === null ? '' : ` · ${ticks} ticks`}${events === null ? '' : ` · ${events} events`}`;
  },
  'agent.created': () => 'joined the world',
  'agent.retired': (p, v) => `retired · ${s(p, 'reason') || v.unknownReason}`,
  'agent.learning.updated': () => 'learning updated',
  'task.created': (p) => {
    const family = s(p, 'family');
    return `task ${s(p, 'task') || 'a task'} created${family === '' ? '' : ` · ${family}`}`;
  },
  'task.expired': (p) => `task ${s(p, 'task')} expired`,
  'submission.verified': (p, v) =>
    `verified a submission · verdict ${p.verdict === true ? v.verdict.valid : p.verdict === false ? v.verdict.invalid : v.verdict.unknown}`,
  'memory.stored': (p) => {
    const key = s(p, 'key');
    return `stored a memory${key === '' ? '' : ` · ${key}`}`;
  },
  'memory.retrieved': (p) => {
    const key = s(p, 'key');
    return `retrieved a memory${key === '' ? '' : ` · ${key}`}`;
  },
  'capability.published': () => 'published a capability',
  'capability.used': (p, v) => `used a capability of ${s(p, 'peer') || v.peer}`,
  'resource.transferred': (p, v) => {
    const amount = n(p, 'amount');
    const resource = s(p, 'resource') || 'resources';
    const peer = s(p, 'peer') || v.peer;
    return amount !== null ? `${amount} ${resource} → ${peer}` : `${resource} transferred → ${peer}`;
  },
  'pressure.applied': (p, v) => `physics pressure · ${s(p, 'type') || v.pressureApplied}`,
  'metrics.recorded': () => 'metrics recorded',
  'tick.completed': (p) => `tick ${n(p, 't') ?? 0} completed`,
  'event.other': (p) => s(p, 'type'),
  'fold.retired': (p) => `retired at t${n(p, 'tick') ?? 0}`,
  'fold.solving': (p) =>
    `solving ${s(p, 'family')} task · t${n(p, 'from') ?? 0}→t${n(p, 'to') ?? 0}`,
  'fold.awaiting': (p) =>
    `awaiting verdict on ${s(p, 'family')} task · t${n(p, 'from') ?? 0}→t${n(p, 'to') ?? 0}`,
  'fold.idle': () => 'idle',
};

const RU: Record<TaskPhraseKey, Template> = {
  'cognition.consulted': () => 'консультация с моделью',
  'cognition.unavailable': () => 'модель недоступна',
  'cognition.starved': () => 'мышление без бюджета',
  'cognition.rejected': (p, v) =>
    `размышление · ${s(p, 'model')}${tier(p)}${tokens(p, v.tokUnit)} → ответ отклонён`,
  'cognition.thought': (p, v) =>
    `размышление · ${s(p, 'model')}${tier(p)}${tokens(p, v.tokUnit)} → ${s(p, 'actions') || v.actionsNone}`,
  'resource.spent': (p, v) => `действие · ${s(p, 'action') || v.unknownAction}`,
  'task.claimed': () => 'взял задачу',
  'task.submitted': () => 'отправил результат',
  'task.evaluated': (p, v) =>
    `задача ${p.accepted === true ? 'принята' : 'отклонена'}${quality(p, v.qualityWord)}`,
  'link.created': (p, v) => `связался с ${s(p, 'peer') || v.peer}`,
  'link.removed': (p, v) => `разорвал связь с ${s(p, 'peer') || v.peer}`,
  'link.used': (p, v) => `связь → ${s(p, 'peer') || v.peer} задействована`,
  'message.sent': (p, v) => `сообщение → ${s(p, 'peer') || v.peer}`,
  'message.delivered': () => 'сообщение получено',
  'violation.recorded': (p, v) => `недопустимое действие · ${s(p, 'reason') || v.rejectedReason}`,
  'run.resumed': (p) => `эпоха продолжена от ${s(p, 'parent')}…`,
  'run.started': () => 'вселенная запущена',
  'run.completed': (p) => {
    const ticks = n(p, 'ticks');
    const events = n(p, 'events');
    return `запуск завершён${ticks === null ? '' : ` · ${ticks} тиков`}${events === null ? '' : ` · ${events} событий`}`;
  },
  'agent.created': () => 'вошёл в мир',
  'agent.retired': (p, v) => `выведен из мира · ${s(p, 'reason') || v.unknownReason}`,
  'agent.learning.updated': () => 'обучение обновлено',
  'task.created': (p) => {
    const family = s(p, 'family');
    return `задача ${s(p, 'task') || 'без имени'} создана${family === '' ? '' : ` · ${family}`}`;
  },
  'task.expired': (p) => `задача ${s(p, 'task')} просрочена`,
  'submission.verified': (p, v) =>
    `проверил решение · вердикт ${p.verdict === true ? v.verdict.valid : p.verdict === false ? v.verdict.invalid : v.verdict.unknown}`,
  'memory.stored': (p) => {
    const key = s(p, 'key');
    return `записал в память${key === '' ? '' : ` · ${key}`}`;
  },
  'memory.retrieved': (p) => {
    const key = s(p, 'key');
    return `прочитал из памяти${key === '' ? '' : ` · ${key}`}`;
  },
  'capability.published': () => 'опубликовал возможность',
  'capability.used': (p, v) => `использовал возможность ${s(p, 'peer') || v.peer}`,
  'resource.transferred': (p, v) => {
    const amount = n(p, 'amount');
    const resource = s(p, 'resource') || 'ресурсы';
    const peer = s(p, 'peer') || v.peer;
    return amount !== null
      ? `передано ${amount} ${resource} → ${peer}`
      : `передан ресурс ${resource} → ${peer}`;
  },
  'pressure.applied': (p, v) => `давление среды · ${s(p, 'type') || v.pressureApplied}`,
  'metrics.recorded': () => 'метрики записаны',
  'tick.completed': (p) => `тик ${n(p, 't') ?? 0} завершён`,
  'event.other': (p) => s(p, 'type'),
  'fold.retired': (p) => `выведен из мира на t${n(p, 'tick') ?? 0}`,
  'fold.solving': (p) =>
    `решает задачу ${s(p, 'family')} · t${n(p, 'from') ?? 0}→t${n(p, 'to') ?? 0}`,
  'fold.awaiting': (p) =>
    `ждёт вердикта по задаче ${s(p, 'family')} · t${n(p, 'from') ?? 0}→t${n(p, 'to') ?? 0}`,
  'fold.idle': () => 'простаивает',
};

const TEMPLATES: Record<Lang, Record<TaskPhraseKey, Template>> = { en: EN, ru: RU };

/** Every key the renderer knows — used by the roster validator to refuse unknown ones. */
export const TASK_PHRASE_KEYS: readonly TaskPhraseKey[] = Object.keys(EN) as TaskPhraseKey[];

/**
 * Phrases that name their own tick inside the sentence and so do not take the
 * trailing `(tN)` reference the rest of the vocabulary carries.
 */
const NO_TICK_SUFFIX = new Set<TaskPhraseKey>([
  'tick.completed',
  'fold.retired',
  'fold.solving',
  'fold.awaiting',
  'fold.idle',
]);

/**
 * Render one phrase in one locale. Unknown keys (a newer server than the
 * browser) degrade to the raw key rather than throwing — the caller keeps a
 * line it can show, and the line is visibly machine-shaped, not a claim.
 */
export function renderTaskPhrase(lang: Lang, phrase: TaskPhrase): string {
  const template = TEMPLATES[lang][phrase.k] as Template | undefined;
  const p: Params = phrase.p ?? {};
  if (template === undefined) return phrase.k;
  const body = template(p, VOCAB[lang]);
  const t = n(p, 't');
  return t === null || NO_TICK_SUFFIX.has(phrase.k) ? body : `${body} (t${t})`;
}
